import { CustomFieldFilter, ListFilter, SortDirection, TextFilter } from "@common/api/filterTypes.ts";
import { DateFieldValue } from "@common/components/filterPopover/subMenues/DateFieldSubMenu.tsx";
import { EntityFieldType } from "@common/entityField.types";
import { OverviewTemplateStepInfo } from "@common/hooks/useOverviewTopTab";
import { ProcessStandardField } from "../entityTemplates/EntityTemplate.types.ts";
import { ProcessColumn } from "./processes/processColumns.ts";

// Mirror of Backend/Core/DomainModels/Processes/ProcessStatus.cs.
export enum ProcessStatus {
    Draft = 1,
    Active = 2,
    Archived = 3,
}

export interface ProcessSearchRequest {
    filter: ProcessFilterRequest;
    displayFields: Array<ProcessFieldOrderItem>;
}

// Mirrors Core/Filtering/Processes/ProcessTableRequest.cs. A displayed field is either one of the built-in columns or
// one of the custom fields a process template defines; the two interleave freely.
export interface ProcessFieldOrderItem {
    column?: ProcessColumn;
    customFieldId?: string;
    displayOrder: number;
}

// Mirrors Core/Filtering/Processes/ExportProcessTableRequest.cs.
export interface ProcessExportHeader {
    header: string;
    column?: ProcessColumn;
    customFieldId?: string;
}

// Mirrors Core/Filtering/Processes/ProcessFilterRequest.cs. searchText sits outside the saved view blob: search is
// deliberately not persisted with a view.
export interface ProcessFilterRequest {
    searchText?: string;
    showOnlyMine?: boolean;

    statuses?: ListFilter<ProcessStatus>;
    responsibleIds?: ListFilter<string>;
    groupEntityIds?: ListFilter<string>;
    tagIds?: ListFilter<string>;
    templateIds?: ListFilter<string>;
    businessAreaIds?: ListFilter<string>;
    complianceAreaIds?: ListFilter<string>;
    assetIds?: ListFilter<string>;
    legalEntityIds?: ListFilter<string>;
    processingActivityIds?: ListFilter<string>;
    riskAreaLevels?: ListFilter<string>;

    createdDateRange?: DateFieldValue;

    description?: TextFilter;
    note?: TextFilter;

    visibleToAllUsers?: boolean;
    additionalUserIds?: ListFilter<string>;
    excludedUserIds?: ListFilter<string>;
    accessBusinessAreaIds?: ListFilter<string>;

    customFieldFilters?: Array<CustomFieldFilter>;

    pageNumber: number;
    pageSize: number;
    sortByColumn?: ProcessColumn;
    // Set instead of sortByColumn when the sorted column is a custom field, which has no enum value to name it.
    sortByCustomFieldId?: string;
    sortDirection: SortDirection;
}

export interface ProcessSearchResult {
    processes: Array<ProcessFilterItem>;
    totalCount: number;
    pageNumber: number;
    pageSize: number;
    totalPages: number;
}

export interface ProcessFilterItem {
    id: string;
    name: string;
    status: ProcessStatus;
    fields: Array<ProcessFieldValue>;
}

// Mirrors Core/Filtering/Processes/ProcessFilterResult.cs. Rendered by DynamicCellRenderer like the other overviews.
export interface ProcessFieldValue {
    value: any;
    type: EntityFieldType;
    column: ProcessColumn;
    // Set instead of column for a cell from one of the template's custom fields.
    customFieldId?: string;
    referencedId?: string;
}

// Mirrors UiWebApi.Application/Processes/ProcessOverviewViewModel.cs.
export interface ProcessOverviewViewModel {
    id: string;
    name: string;
    description?: string;
    userFriendlyId: number;
    template: string;
    entityTemplateId: string;
    note?: string;
    templateSteps: Array<OverviewTemplateStepInfo>;
    overviewFields: Array<ProcessStandardField>;
    overviewFieldValues: Array<ProcessFieldValue>;
    numberOfUsersWithAccess: number;
    assets: Array<ProcessLinkedEntity>;
    legalEntities: Array<ProcessLinkedLegalEntity>;
    processingActivities: Array<ProcessLinkedEntity>;
}

export interface ProcessLinkedEntity {
    id: string;
    name: string;
}

export interface ProcessLinkedLegalEntity extends ProcessLinkedEntity {
    isCustomerOwned: boolean;
}
